import React from 'react';
import { motion } from 'framer-motion';
import { Users, UserPlus, Briefcase, Search, Handshake, FileText } from 'lucide-react';

const WhatWeDo = () => {
  // Defined based on the context of your color scheme
  const primaryBg = '#1F305E'; 

  const mainServices = [
    { 
      name: "Temporary Staffing", 
      description: "Flexible workforce solutions to meet seasonal demands and short-term project requirements with qualified professionals.", 
      icon: <Users />, 
      link: "/services/temporary-staffing",
      cardBg: '#e8eaf6',
      iconBg: '#c5cae9',
      iconColor: '#3f51b5'
    },
    { 
      name: "Permanent Placement", 
      description: "Strategic hiring of long-term employees who align with your company culture and drive sustained business growth.", 
      icon: <UserPlus />, 
      link: "/services/permanent-recruitment",
      cardBg: '#e8f5e9',
      iconBg: '#c8e6c9',
      iconColor: '#4caf50'
    },
    { 
      name: "Contract Staffing", 
      description: "Specialized talent for defined projects and durations, providing expertise exactly when and where you need it.", 
      icon: <Briefcase />, 
      link: "/services/contract-staffing",
      cardBg: '#fff3e0',
      iconBg: '#ffe0b2',
      iconColor: '#ff9800'
    },
    { 
      name: "Statement of Work", 
      description: "Outcome-based workforce solutions where we manage project delivery, resources, timelines, and agreed deliverables under a defined Statement of Work.", 
      icon: <FileText />, 
      link: "/services/statement-of-work",
      cardBg: '#fce4ec',
      iconBg: '#f8bbd0',
      iconColor: '#e91e63'
    }
  ];

  const secondaryServices = [
    { name: "Executive Search", description: "High-level placement for leadership roles.", icon: <Search /> },
    { name: "Talent Acquisition", description: "Strategic support for your internal hiring teams.", icon: <Users /> },
    { name: "RPO Services", description: "Recruitment Process Outsourcing for efficiency.", icon: <Handshake /> }
  ];

  return (
    <section className="py-12" style={{ backgroundColor: '#ffffff' }}>
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-16 lg:mb-6"
        >
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-semibold leading-tight tracking-tight">
            What We Do
          </h2>
          <p className="max-w-2xl text-left text-lg" style={{ color: '#1F305E' }}>
            End-to-end staffing solutions designed to help businesses scale efficiently and stay 
            competitive in a rapidly changing marketplace.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {mainServices.map((service, idx) => (
            <motion.div
              key={idx}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: idx * 0.1 }}
              className="p-8 rounded-2xl shadow-sm transition-all duration-300 hover:transform hover:-translate-y-2 group cursor-default"
              style={{ backgroundColor: service.cardBg }}
            >
              <div 
                className="w-12 h-12 rounded-xl flex items-center justify-center mb-6 shadow-lg" 
                style={{ backgroundColor: service.iconBg, color: service.iconColor }}
              >
                {React.cloneElement(service.icon, { size: 24 })}
              </div>
              <h3 className="text-xl font-bold mb-3" style={{ color: '#000000' }}>
                {service.name}
              </h3>
              <p 
                className="text-[15px] xl:text-[16px] leading-[1.6]" 
                style={{ 
                  color: "rgba(19, 18, 18, 0.7)",
                  wordBreak: 'normal',
                  overflowWrap: 'normal',
                  hyphens: 'none',
                  whiteSpace: 'normal' 
                }}
              >
                {service.description}
              </p>
            </motion.div>
          ))}
        </div>

        <div className="grid lg:grid-cols-3 border-t border-slate-100 mt-2">
          {secondaryServices.map((service, idx) => (
            <div 
              key={idx} 
              className={`p-8 flex items-start gap-4 ${idx !== 0 ? 'lg:border-l' : ''}`}
              style={{ borderColor: '#f1f5f9' }}
            >
              <div className="mt-1 flex-shrink-0" style={{ color: primaryBg }}>
                {React.cloneElement(service.icon, { size: 20 })}
              </div>
              <div>
                <h3 className="text-xl font-bold mb-3" style={{ color: '#000000' }}>
                  {service.name}
                </h3>
                <p className="text-[15px] xl:text-[16px] leading-[1.6]" style={{ color: "rgba(19, 18, 18, 0.7)" }}>
                  {service.description}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default WhatWeDo;